// src/pages/markets/board/board/ColumnHeader.tsx

import { useMemo, useState } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    IconButton,
    Tooltip,
    Popover,
    Badge,
    ToggleButtonGroup,
    ToggleButton,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TuneIcon from '@mui/icons-material/Tune';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ViewAgendaOutlinedIcon from '@mui/icons-material/ViewAgendaOutlined';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ColumnFilters } from './ColumnFilters';
import { CategoryMenu } from './CategoryMenu';
import {
    ColumnInfoPanel,
    COLUMN_INFO_PANEL_MIN_HEIGHT,
    COLUMN_INFO_PANEL_WIDTH,
} from './ColumnInfoPanel';
import { HoverPanel } from '../card/HoverPanel';
import { getMetric } from '../api/metrics';
import { fieldSpec } from '../api/useSchema';
import type { ActivityScope, BoardFilters, BoardSchema } from '../api/boardTypes';
import type { BodyMode, CardView, ColumnConfig } from './columnConfig';
import type { CategoryConfig } from './categories';
import {
    TOOLBAR_HEIGHT,
    toolbarIconButtonSx,
    toolbarSelectSx,
    toolbarToggleSx,
} from '../../../../components/common/page/toolbarStyles';
import { numSx } from '../../../../components/common/page/numberStyles';
import { HIDDEN_SORT_KEYS } from './sortKeys';

export interface ColumnHeaderProps {
    config: ColumnConfig;
    schema: BoardSchema;
    filters: BoardFilters;
    sortKeys: string[];
    bodyMode: BodyMode;
    cardView: CardView;
    eventCount: number;
    pendingCount: number;
    isFetching: boolean;
    filtersDirty: boolean;
    lastError: Error | null;
    lastErrorAt: number;
    now: number;
    onFiltersChange: (patch: Partial<BoardFilters>) => void;
    onReset: () => void;
    onShare: () => string;
    onBodyModeChange: (mode: BodyMode) => void;
    onCardViewChange: (view: CardView) => void;
    onApplyPending: () => void;
    onRetry: () => void;
    onFocusEvent?: (eventId: string) => void;
    onCategoryChange: (category: CategoryConfig) => void;
}

const SCOPE_LABELS: Record<string, string> = {
    market: 'Market',
    event: 'Event',
};

const SCOPE_TITLES: Record<string, string> = {
    market: 'Rank each market leg on its own',
    event: 'Fold the legs into one number per event',
};

function formatErrorTime(at: number): string {
    if (!at) return '';
    return new Date(at).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });
}

export function ColumnHeader({
    config,
    schema,
    filters,
    sortKeys,
    bodyMode,
    cardView,
    eventCount,
    pendingCount,
    isFetching,
    filtersDirty,
    lastError,
    lastErrorAt,
    now,
    onFiltersChange,
    onReset,
    onShare,
    onBodyModeChange,
    onCardViewChange,
    onApplyPending,
    onRetry,
    onFocusEvent,
    onCategoryChange,
}: ColumnHeaderProps) {
    const theme = useTheme();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [categoryAnchor, setCategoryAnchor] = useState<HTMLElement | null>(null);

    // In the header rather than the panel: this one gets flipped constantly, so
    // it should not cost two clicks. It is the same filter state either way, so
    // it shares, persists and refetches like everything else.
    const scopeSpec = useMemo(
        () => fieldSpec(schema, config.source, 'scope'),
        [schema, config.source],
    );

    const shownSortKeys = useMemo(
        () => sortKeys.filter(k => !HIDDEN_SORT_KEYS.has(k) || k === filters.sortBy),
        [sortKeys, filters.sortBy],
    );

    const errorTime = formatErrorTime(lastErrorAt);
    const errorTitle = errorTime ? `Last update failed · ${errorTime}` : 'Last update failed';

    return (
        <Box
            sx={{
                px: 1,
                py: 0.75,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                borderBottom: 1,
                borderColor: 'divider',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    minHeight: TOOLBAR_HEIGHT,
                }}
            >
                <HoverPanel
                    placement="bottom-start"
                    padded={false}
                    minWidth={COLUMN_INFO_PANEL_WIDTH}
                    minHeight={COLUMN_INFO_PANEL_MIN_HEIGHT}
                    content={() => (
                        <ColumnInfoPanel
                            schema={schema}
                            column={config.source}
                            // Only a group pane's title can carry a suffix; a
                            // discovery pane keeps the panel's own column label.
                            title={config.group !== null ? config.title : undefined}
                            filters={filters}
                            now={now}
                            onFiltersChange={onFiltersChange}
                            onFocusEvent={onFocusEvent}
                        />
                    )}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        flex: '0 1 auto',
                        cursor: 'help',
                    }}
                >
                    <Box
                        role="button"
                        tabIndex={0}
                        aria-haspopup="menu"
                        aria-label={`Category: ${config.title}`}
                        onClick={e => setCategoryAnchor(e.currentTarget)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setCategoryAnchor(e.currentTarget);
                            }
                        }}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            minWidth: 0,
                            maxWidth: '100%',
                            px: 0.4,
                            borderRadius: '3px',
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 700,
                                letterSpacing: '-0.01em',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                borderBottom: '1px dotted',
                                borderColor: 'divider',
                            }}
                        >
                            {config.title}
                        </Typography>
                        <KeyboardArrowDownIcon sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }} />
                    </Box>
                </HoverPanel>
                <Typography sx={{ fontSize: '12px', color: 'text.secondary', flexShrink: 0, ...numSx }}>
                    {eventCount > 0 ? `${eventCount}` : ''}
                </Typography>

                {pendingCount > 0 && (
                    <Box
                        onClick={onApplyPending}
                        sx={{
                            cursor: 'pointer',
                            fontSize: '11px',
                            fontWeight: 700,
                            px: 0.75,
                            py: 0.25,
                            borderRadius: '4px',
                            color: 'primary.main',
                            bgcolor: alpha(theme.palette.primary.main, 0.14),
                            whiteSpace: 'nowrap',
                            flexShrink: 0,
                        }}
                    >
                        {pendingCount} new
                    </Box>
                )}

                <Box sx={{ flex: 1 }} />

                {lastError && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, flexShrink: 0 }}>
                        <Tooltip title={errorTitle}>
                            <ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />
                        </Tooltip>
                        <Tooltip title="Reload">
                            <IconButton
                                size="small"
                                onClick={onRetry}
                                disabled={isFetching}
                                sx={{ p: 0.25 }}
                            >
                                <RefreshIcon
                                    sx={{
                                        fontSize: 16,
                                        '@keyframes boardSpin': {
                                            from: { transform: 'rotate(0deg)' },
                                            to: { transform: 'rotate(360deg)' },
                                        },
                                        animation: isFetching
                                            ? 'boardSpin 1s linear infinite'
                                            : 'none',
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {isFetching && !lastError && (
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: 'primary.main',
                            opacity: 0.7,
                            flexShrink: 0,
                        }}
                    />
                )}

                {scopeSpec && (
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={filters.scope ?? scopeSpec.default ?? 'market'}
                        onChange={(_, v: ActivityScope | null) => {
                            if (v) onFiltersChange({ scope: v });
                        }}
                        sx={{ flexShrink: 0, ...toolbarToggleSx }}
                    >
                        {(scopeSpec.options ?? []).map(opt => (
                            <ToggleButton key={opt} value={opt}>
                                <Tooltip title={SCOPE_TITLES[opt] ?? ''}>
                                    <span>{SCOPE_LABELS[opt] ?? opt}</span>
                                </Tooltip>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Select
                    value={sortKeys.includes(filters.sortBy) ? filters.sortBy : ''}
                    onChange={e => onFiltersChange({ sortBy: e.target.value })}
                    size="small"
                    variant="outlined"
                    displayEmpty
                    aria-label="Sort by"
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        ...toolbarSelectSx,
                    }}
                >
                    {shownSortKeys.map(key => (
                        <MenuItem key={key} value={key} sx={{ fontSize: '12px' }}>
                            {getMetric(key).label}
                        </MenuItem>
                    ))}
                </Select>

                <Tooltip title={filters.sortDir === 'desc' ? 'Highest first' : 'Lowest first'}>
                    <IconButton
                        size="small"
                        onClick={() =>
                            onFiltersChange({ sortDir: filters.sortDir === 'desc' ? 'asc' : 'desc' })
                        }
                        sx={toolbarIconButtonSx}
                    >
                        {filters.sortDir === 'desc' ? (
                            <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                </Tooltip>

                <ToggleButtonGroup
                    value={bodyMode}
                    exclusive
                    size="small"
                    onChange={(_, v) => v && onBodyModeChange(v as BodyMode)}
                    sx={{ flexShrink: 0, ...toolbarToggleSx }}
                >
                    <ToggleButton value="view">
                        <Tooltip title="Current state">
                            <ViewAgendaOutlinedIcon sx={{ fontSize: 16 }} />
                        </Tooltip>
                    </ToggleButton>
                    <ToggleButton value="trend">
                        <Tooltip title="Trend">
                            <ShowChartIcon sx={{ fontSize: 16 }} />
                        </Tooltip>
                    </ToggleButton>
                </ToggleButtonGroup>

                <Tooltip title={cardView === 'card' ? 'Fold all to rows' : 'Expand all cards'}>
                    <IconButton
                        size="small"
                        onClick={() => onCardViewChange(cardView === 'card' ? 'row' : 'card')}
                        sx={toolbarIconButtonSx}
                    >
                        {cardView === 'card' ? (
                            <UnfoldLessIcon sx={{ fontSize: 16 }} />
                        ) : (
                            <UnfoldMoreIcon sx={{ fontSize: 16 }} />
                        )}
                    </IconButton>
                </Tooltip>

                <Tooltip title="Filters">
                    <IconButton
                        size="small"
                        onClick={e => setAnchor(e.currentTarget)}
                        sx={toolbarIconButtonSx}
                    >
                        <Badge
                            variant="dot"
                            invisible={!filtersDirty}
                            color="primary"
                            sx={{ '& .MuiBadge-dot': { minWidth: 6, height: 6 } }}
                        >
                            <TuneIcon sx={{ fontSize: 16 }} />
                        </Badge>
                    </IconButton>
                </Tooltip>
            </Box>

            <Popover
                open={Boolean(anchor)}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <ColumnFilters
                    schema={schema}
                    column={config.source}
                    effective={filters}
                    onChange={onFiltersChange}
                    onReset={onReset}
                    onShare={onShare}
                />
            </Popover>

            <CategoryMenu
                anchorEl={categoryAnchor}
                schema={schema}
                current={{ source: config.source, group: config.group }}
                onPick={category => {
                    setCategoryAnchor(null);
                    onCategoryChange(category);
                }}
                onClose={() => setCategoryAnchor(null)}
            />
        </Box>
    );
}
